import { useCallback, useEffect, useState } from 'react';
import { Button, Icon } from '@mercator-gcp/grout';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import Tooltip from 'components/elements/Tooltip';
import { FlexRow } from 'components/layout/FlexRow';
import { ShareGroupConfig } from 'components/modals/ShareGroupConfig';
import { isChatCreatedSavedSearch } from 'components/widgets/ProjectInsights/Toolbar/getSavedSearchEditorType';
import { useCompanyCsvExport } from 'hooks/useCompanyCsvExport';
import { useSelectedLeadFilters } from 'hooks/useLeadTableFiltering';
import { useProjectCsvExport } from 'hooks/useProjectCsvExport';
import { trackGroupConfigAction } from 'services/analytics/trackGroupConfigAction';
import {
  trackImportCompanies,
  ImportCompaniesSuccessStates,
} from 'services/analytics/trackImportCompanies';
import { useDashboardGroupType, groupAtom } from 'state/dashboard';
import { GroupRawResponse } from 'types';
import { trackExportProjectList } from 'utils/analytics';
import {
  CompanyGroupsPageViewSections,
  CompanyGroupsPageViewSubSections,
  ExportProjectListGroupTypes,
  ExportProjectListSuccessStates,
} from 'utils/analytics/constants';
import { GROUP_TYPE } from 'utils/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import {
  buildDashboardURLWithParams,
  buildProjectMapURLWithParams,
  savedSearchEditURL,
} from 'utils/navigation';
import { groupRawToGroup } from 'utils/transforms';

const CSV_EXPORT_BUTTON_TEXT = 'Export to CSV';

type Props = {
  id: string | number;
  group: GroupRawResponse | null;
  showViewInMapButton?: boolean;
};

const GroupSettingsMenu = ({ id, group, showViewInMapButton = true }: Props) => {
  const navigate = useNavigate();
  const { selectedLeadFilters } = useSelectedLeadFilters();
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const {
    fetchData: fetchProjectData,
    loading: projectCsvLoading,
    data: projectCsvData,
  } = useProjectCsvExport({
    id: String(id),
    activeTabs: selectedLeadFilters,
  });
  const {
    fetchData: fetchCompanyData,
    loading: companyCsvLoading,
    data: companyCsvData,
  } = useCompanyCsvExport(String(id));

  const [type] = useDashboardGroupType();

  const isProjectGroup = type === GROUP_TYPE.PROJECT;
  const isCompanyGroup = type === GROUP_TYPE.COMPANY;
  const isAiSearch = isChatCreatedSavedSearch(group);

  const containerRow = {
    gap: 'padding_8',
  };

  const [, setGroup] = useAtom(groupAtom);

  const trackCsvExport = ({
    linkPath,
    clickText,
  }: {
    linkPath?: string;
    clickText?: string;
  }) => {
    const getGroupType = () => {
      if (isProjectGroup) {
        return ExportProjectListGroupTypes.PROJECT_GROUP;
      }
      if (isCompanyGroup) {
        return ExportProjectListGroupTypes.COMPANY_GROUP;
      }

      return ExportProjectListGroupTypes.UNCATEGORIZED;
    };

    trackExportProjectList({
      groupId: String(id),
      groupType: getGroupType(),
      linkPath: linkPath || undefined,
      clickText: clickText || undefined,
      successState: linkPath
        ? ExportProjectListSuccessStates.SUCCESS
        : ExportProjectListSuccessStates.SENT,
    });
  };

  useEffect(() => {
    if (isProjectGroup && !projectCsvLoading && projectCsvData) {
      trackCsvExport({ linkPath: projectCsvData });
    }
  }, [isProjectGroup, projectCsvData, projectCsvLoading]);

  useEffect(() => {
    if (isCompanyGroup && !companyCsvLoading && companyCsvData) {
      trackCsvExport({ linkPath: companyCsvData });
    }
  }, [isCompanyGroup, companyCsvLoading, companyCsvData]);

  const onClick = useCallback(() => {
    // Set the group to the current group ID
    if (group) {
      setGroup(groupRawToGroup(group));
      trackGroupConfigAction({
        group_id: String(group.id),
        group_type: isProjectGroup ? 'project_group' : 'company_group',
        click_action: 'edit_group',
      });
      navigate(savedSearchEditURL(Number(id)));
    }
  }, [id, navigate, group, setGroup]);

  return (
    <FlexRow sx={containerRow}>
      {showViewInMapButton && (
        <Button
          variant="primary"
          startAdornment={<Icon name={Icon.Name.Map} />}
          onClick={() => navigate(buildProjectMapURLWithParams({ groupId: Number(id) }))}
        >
          View in map
        </Button>
      )}
      <Button
        onClick={onClick}
        variant="functional"
        startAdornment={<Icon name={Icon.Name.Gear} />}
      >
        Edit search filters
      </Button>
      {isProjectGroup && (
        <Button
          variant="secondary"
          startAdornment={!projectCsvLoading && <Icon name={Icon.Name.Download} />}
          isLoading={projectCsvLoading}
          onClick={() => {
            trackCsvExport({ clickText: CSV_EXPORT_BUTTON_TEXT });
            fetchProjectData();
          }}
        >
          {CSV_EXPORT_BUTTON_TEXT}
        </Button>
      )}
      {isCompanyGroup && (
        <Button
          variant="secondary"
          startAdornment={!companyCsvLoading && <Icon name={Icon.Name.Download} />}
          isLoading={companyCsvLoading}
          onClick={() => {
            trackCsvExport({ clickText: CSV_EXPORT_BUTTON_TEXT });
            fetchCompanyData();
          }}
        >
          {CSV_EXPORT_BUTTON_TEXT}
        </Button>
      )}
      {isCompanyGroup && (
        <Button
          variant="secondary"
          startAdornment={<Icon name={Icon.Name.AddToList} />}
          onClick={() => {
            trackImportCompanies({
              group_id: String(id),
              section_name: CompanyGroupsPageViewSections.COMPANY_GROUPS,
              sub_section_name: CompanyGroupsPageViewSubSections.IMPORT,
              click_text: 'Import Companies',
              success_state: ImportCompaniesSuccessStates.START,
            });
            const base = buildDashboardURLWithParams({
              groupId: Number(id),
              groupType: GROUP_TYPE.COMPANY,
            });
            navigate(`${base}&${QUERY_PARAM.importCompanies}=companies`);
          }}
        >
          Import companies
        </Button>
      )}
      <Tooltip
        tooltipText="Sharing is not yet available for AI content"
        shouldRenderTooltip={isAiSearch}
      >
        <Button
          variant="secondary"
          onClick={() => setIsShareModalOpen(true)}
          startAdornment={<Icon name={Icon.Name.AddUser} />}
          disabled={isAiSearch}
        >
          Share
        </Button>
      </Tooltip>
      <ShareGroupConfig
        id={String(id)}
        isShareModalOpen={isShareModalOpen}
        setIsShareModalOpen={setIsShareModalOpen}
        onCancelClick={() => setIsShareModalOpen(false)}
      />
    </FlexRow>
  );
};

export default GroupSettingsMenu;
